import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { SPACE_ROLES } from '../auth/permissions';
import { usePermissions } from './usePermissions';
import { useSpaceId } from './useSpaceId';
import { USER_ANALYTICS_ROUTE } from '../constants/userAnalytics';

/**
 * Who sees which filters, and the one place that decides what a request may carry.
 *
 * A global admin sees every control and sends whatever filters are set. A marketing manager
 * sees no filter controls and every request carries the space they have open as `spaceId` —
 * read from AuthContext state at render, never from localStorage at call time (localStorage
 * is shared between browser tabs, so a second tab on another space would silently switch this
 * tab's data). They never send `entityId` or `emailDomain`; the API rejects both.
 */
export function useAnalyticsScope() {
  const { user, activeSpace } = useAuth();
  const { isGlobalAdmin, canFeature } = usePermissions();
  const openSpaceId = useSpaceId();

  const restricted = !isGlobalAdmin;
  // activeSpace.roles is a saved copy that can be older than the roles from the last sign-in
  // (user.spaces), which is what the API checks. Trust the sign-in roles so a stale copy never fires requests.
  const signInRoles = user?.spaces?.find((s) => s.spaceId === openSpaceId)?.roles ?? [];
  const hasAccess = !restricted || (openSpaceId != null && signInRoles.includes(SPACE_ROLES.MARKETING_MANAGER));
  const showEntitySpaceFilter = canFeature(USER_ANALYTICS_ROUTE, 'entity-space-filter');
  const showEmailDomainFilter = canFeature(USER_ANALYTICS_ROUTE, 'email-domain-filter');

  const toRequestFilters = useCallback((filters) => {
    const range = filters.period === 'custom' ? { from: filters.from, to: filters.to } : {};
    if (restricted) return { spaceId: openSpaceId, period: filters.period, ...range };
    return {
      entityId: filters.entityId,
      spaceId: filters.spaceId,
      emailDomain: filters.emailDomain,
      period: filters.period,
      ...range,
    };
  }, [restricted, openSpaceId]);

  return {
    restricted,
    showEntitySpaceFilter,
    showEmailDomainFilter,
    effectiveSpaceId: restricted ? openSpaceId : null,
    spaceName: restricted ? (activeSpace?.spaceName || (openSpaceId != null ? `Space ${openSpaceId}` : '')) : '',
    // No access (not a global admin, and no marketing_manager role in the open space) means nothing is requested.
    ready: hasAccess,
    // Data fetched for one space must never be shown (even dimmed) for another.
    resetKey: restricted ? `space:${openSpaceId}` : 'global',
    toRequestFilters,
  };
}
